import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Search, RotateCcw, FileSpreadsheet, CheckCircle, Loader2,
  X, Check, AlertCircle, Info,
  ChevronUp, ChevronDown, ChevronsUpDown, ArrowUpDown,
} from 'lucide-react';

import Breadcrumb, { BREADCRUMB_EXPORT_BTN } from '../../components/Breadcrumb/Breadcrumb';
import {
  fetchOTDetailsWithStatus,
  approveOTRecord,
  bulkApproveOTRecords,
  clearBulkApprovalResult,
} from '../../store/slices/overtimeSlice';
import { fetchAllEmployees } from '../../store/slices/employeeSlice';
import { mapOTRows } from '../../utils/overtimeAnalytics';
import { exportOTRecordsToExcel } from '../../utils/otExcel';
import EmployeeSearch from '../../components/common/EmployeeSearch';

import { fetchOTAttendanceForMonth } from '../../store/slices/filterAttendanceSlice';

const FIELD =
  'w-full h-9 bg-white border border-gray-200 rounded-lg text-xs px-2.5 outline-none focus:border-primary-500 text-gray-700';
const LABEL = 'text-[11px] font-medium text-gray-500 mb-1 block';

const STICKY_TOP = -25;


const fmtH = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? `${Number(n.toFixed(2))} h` : '—';
};

const fmtLateHours = (minutes) => {
  const n = Number(minutes);
  if (!Number.isFinite(n) || n <= 0) return '—';
  return `${Number((n / 60).toFixed(2))}h`;
};

const SCROLL_STYLES = `
  .ot-table-scroll { scrollbar-width: none; -ms-overflow-style: none; }
  .ot-table-scroll::-webkit-scrollbar { display: none; }
  .ot-fixed-scroll { scrollbar-width: auto; scrollbar-color: #2b8d4f #f3f4f6; }
  .ot-fixed-scroll::-webkit-scrollbar { height: 14px; }
  .ot-fixed-scroll::-webkit-scrollbar-track { background: #f3f4f6; }
  .ot-fixed-scroll::-webkit-scrollbar-thumb { background: #2b8d4f; border-radius: 7px; border: 2px solid #f3f4f6; }
  .ot-fixed-scroll::-webkit-scrollbar-thumb:hover { background: #2b8d4f; }
`;

// ─────────────────────────────────────────────────────────────────
// Column widths — tightened, and the unused "Pre & Post OT Hours"
// column (220px) removed. It was still in colgroup but not rendered
// in <thead>/<tbody>, which shifted every column after Post OT by
// one cell.
// ─────────────────────────────────────────────────────────────────
const COLUMN_WIDTHS = [
  44,   // checkbox
  200,  // Employee
  110,  // Date
  130,  // OT type
  90,   // In time
  90,   // Out time
  110,  // Late hours
  110,  // Total hours
  110,  // Normal hours
  110,  // Pre OT hours
  120,  // Post OT hours
  130,  // Holiday OT hours
  130,  // Sunday OT hours
  110,  // OT hours
  90,   // OT rate
  110,  // OT amount
  100,  // Status
  100,  // Actions
];
const sumWidths = (list) => list.reduce((sum, w) => sum + w, 0);
const tableStyleFor = (widths) => ({
  width: '100%',
  minWidth: sumWidths(widths),
  tableLayout: 'fixed',
});

const PINNED_COLS = 6;
const PINNED_WIDTH = sumWidths(COLUMN_WIDTHS.slice(0, PINNED_COLS));
const SCROLL_WIDTHS = COLUMN_WIDTHS.slice(PINNED_COLS);

// Cumulative sticky-left positions — derived from widths so they
// can never drift when a width changes.
const PINNED_LEFT = (() => {
  const arr = [];
  let cursor = 0;
  for (let i = 0; i < PINNED_COLS; i++) {
    arr.push(cursor);
    cursor += COLUMN_WIDTHS[i];
  }
  return arr;
})();

const ColGroup = ({ widths = COLUMN_WIDTHS }) => (
  <colgroup>
    {widths.map((w, i) => (
      <col key={i} style={{ width: w }} />
    ))}
  </colgroup>
);

/* ── Portal-based tooltip ── */
function InfoTip({ children }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);

  const handleEnter = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const tooltipW = 256;
    const pad = 8;
    const left = Math.min(
      Math.max(rect.left + rect.width / 2 - tooltipW / 2, pad),
      window.innerWidth - tooltipW - pad
    );
    setCoords({ top: rect.bottom + 8, left });
    setOpen(true);
  };
  const handleLeave = () => setOpen(false);

  return (
    <>
      <span
        ref={triggerRef}
        className="inline-flex items-center"
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
      >
        <Info size={11} className="text-slate-400 hover:text-slate-600 cursor-help" />
      </span>
      {open &&
        createPortal(
          <span
            role="tooltip"
            className="pointer-events-none fixed z-[9999] w-64
                       rounded-lg bg-slate-900 text-white text-[11px] leading-snug font-normal
                       px-3 py-2 shadow-lg normal-case tracking-normal text-left"
            style={{ top: coords.top, left: coords.left }}
          >
            {children}
            <span
              className="absolute w-2 h-2 bg-slate-900"
              style={{ top: -4, left: '50%', transform: 'translateX(-50%) rotate(45deg)' }}
            />
          </span>,
          document.body
        )}
    </>
  );
}

/* ── stat card used in the totals strip above the table ── */
function StatCard({ label, value, tone = 'slate' }) {
  const palette = {
    slate: 'border-slate-200 bg-white',
    emerald: 'border-emerald-200 bg-emerald-50',
  };
  const textPalette = {
    slate: 'text-slate-900',
    emerald: 'text-emerald-700',
  };
  return (
    <div className={`rounded-lg border px-2.5 py-1.5 shadow-sm ${palette[tone]}`}>
      <p className="text-[10px] font-medium leading-tight text-slate-500">{label}</p>
      <p className={`mt-0.5 text-sm font-bold tabular-nums leading-tight ${textPalette[tone]}`}>
        {value}
      </p>
    </div>
  );
}

const StatusBadge = ({ isApproved }) => {
  if (isApproved) {
    return (
      <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
        Approved
      </span>
    );
  }
  return (
    <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
      Pending
    </span>
  );
};

const TABS = ['All', 'Pending', 'Approved'];

const toDateKey = (value) => {
  if (!value) return '';
  const text = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) {
    return text.slice(0, 10);
  }
  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// First day of current month
const defaultRange = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return { from: `${y}-${m}-01`, to: `${y}-${m}-${d}` };
};

export default function OTDetails() {
  const dispatch = useDispatch();
  const {
    rows,
    pendingRows,
    approvedRows,
    loading,
    approving,
    error,
    approvalError,
    bulkApprovalResult,
  } = useSelector((s) => s.overtime);

  const { employees: reduxEmployees, loading: employeesLoading } = useSelector(
    (s) => s.employees || { employees: [], loading: false }
  );

  const currentUserId = useSelector((s) => s.auth?.user?.employeeId || '0004086');

  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const [employeeId, setEmployeeId] = useState('');
  const [attMonth, setAttMonth] = useState('');
  const [activeTab, setActiveTab] = useState('Pending');
  const [typeFilter, setTypeFilter] = useState('All');
  const [search, setSearch] = useState('');

  const [selectedRows, setSelectedRows] = useState([]);

  const [sort, setSort] = useState({ key: 'date', dir: 'asc' });

  const wrapRef = useRef(null);
  const headRef = useRef(null);
  const barRef = useRef(null);
  const [scrollWidth, setScrollWidth] = useState(0);
  const [hasOverflow, setHasOverflow] = useState(false);
  const syncingBar = useRef(false);

  const isDateActive = sort.key === 'date';

  const [dateMode, setDateMode] = useState('month');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');


  const ALL_EMPLOYEES = '__ALL__';

  const isAllEmployees = (emp) => {
    if (!emp) return false;
    const id = String(emp.employeeId ?? '').trim();
    return !id || id.toUpperCase() === ALL_EMPLOYEES;
  };

  const resolveEmployeeId = () => {
    if (selectedEmployee && !isAllEmployees(selectedEmployee)) {
      return String(selectedEmployee.employeeId).trim();
    }
    const raw = employeeId.trim();
    return raw && raw.toUpperCase() !== ALL_EMPLOYEES ? raw : '';
  };

  const { otAttendanceRows = [] } = useSelector((s) => s.filterAttendance || {});

  

  useEffect(() => {
    dispatch(fetchAllEmployees());
  }, [dispatch]);

  useEffect(() => {
    if (reduxEmployees && Array.isArray(reduxEmployees) && reduxEmployees.length > 0) {
      const normalizedEmployees = reduxEmployees
        .map((emp) => ({
          employeeId: String(emp.HED_EMPLOYEE_ID || emp.HED_SERVICE_NO || ''),
          serviceNo: emp.HED_SERVICE_NO || '',
          name:
            emp.HED_REPORT_NAME ||
            `${emp.HED_FIRST_NAME || ''} ${emp.HED_LAST_NAME || ''}`.trim() ||
            `Employee ${emp.HED_SERVICE_NO}`,
          department: emp.HDD_DEPARTMENT_NAME || '',
          location: emp.HED_LOCATION_ID || '',
          firstName: emp.HED_FIRST_NAME || '',
          lastName: emp.HED_LAST_NAME || '',
          reportName: emp.HED_REPORT_NAME || '',
        }))
        .filter((emp) => emp.employeeId);

      setEmployees(normalizedEmployees);
    }
  }, [reduxEmployees]);

  useEffect(() => {
    const now = new Date();
    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    setAttMonth(currentMonth);
    const { from, to } = defaultRange();
    setDateFrom(from);
    setDateTo(to);
  }, []);

  const handleSearch = () => {
    const params = buildFilterParams();
    if (!params) return;
    setSelectedRows([]);
    dispatch(fetchOTDetailsWithStatus(params));
  };

  useEffect(() => {
    const params = buildFilterParams();
    if (!params) return;
    setSelectedRows([]);
    dispatch(fetchOTDetailsWithStatus(params));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEmployee, dateMode, attMonth, dateFrom, dateTo, dispatch]);

  const getMonthsInRange = (from, to) => {
    const out = [];
    const start = new Date(from);
    const end = new Date(to);
    const cur = new Date(start.getFullYear(), start.getMonth(), 1);
    while (cur <= end) {
      out.push({ year: cur.getFullYear(), month: cur.getMonth() + 1 });
      cur.setMonth(cur.getMonth() + 1);
    }
    return out;
  };

  const getFilteredRows = () => {
    switch (activeTab) {
      case 'Pending':
        return pendingRows;
      case 'Approved':
        return approvedRows;
      default:
        return rows;
    }
  };

  const currentRows = getFilteredRows();

  const attendanceServiceNos = useMemo(() => {
    return [
      ...new Set(
        currentRows
          .map((r) => {
            const employee = employees.find(
              (emp) => String(emp.employeeId) === String(r.HOT_EMPLOYEE_ID)
            );
            return employee?.serviceNo;
          })
          .filter(Boolean)
      ),
    ];
  }, [currentRows, employees]);

  useEffect(() => {
    if (!attendanceServiceNos.length) return;

    if (dateMode === 'month') {
      if (!attMonth) return;
      const [year, month] = attMonth.split('-');
      dispatch(fetchOTAttendanceForMonth({ year, month, serviceNos: attendanceServiceNos }));
    } else {
      if (!dateFrom || !dateTo) return;
      getMonthsInRange(dateFrom, dateTo).forEach(({ year, month }) => {
        dispatch(fetchOTAttendanceForMonth({ year, month, serviceNos: attendanceServiceNos }));
      });
    }
  }, [dateMode, attMonth, dateFrom, dateTo, attendanceServiceNos, dispatch]);

  const handleSort = (key) => {
    if (!key) return;
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'asc' }
    );
  };

  const toggleDateSort = () => {
    setSort((prev) =>
      prev.key === 'date'
        ? { key: 'date', dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key: 'date', dir: 'desc' }
    );
  };

  const handleReset = () => {
    setEmployeeId('');
    setSelectedEmployee(null);
    setDateMode('month');
    setAttMonth('');
    const { from, to } = defaultRange();
    setDateFrom(from);
    setDateTo(to);
    setActiveTab('Pending');
    setTypeFilter('All');
    setSearch('');
    setSelectedRows([]);
    dispatch(clearBulkApprovalResult());
  };


  const mapped = useMemo(() => {
    const otRows = mapOTRows(currentRows, employees);

    return otRows.flatMap((r) => {
      const employee = employees.find(
        (emp) => String(emp.employeeId) === String(r.employeeId)
      );

      const serviceNo = String(employee?.serviceNo || '').trim();
      const otDate = toDateKey(r.date);

      const attendance = otAttendanceRows.find(
        (a) =>
          String(a.ServiceNo || '').trim() === serviceNo &&
          toDateKey(a.AttDate) === otDate
      );

      if (!attendance) return [];

      return [{
        ...r,
        inTime: attendance.InTime || '',
        outTime: attendance.OutTime || '',
        totalHours: attendance.TotalHours ?? '0.00',
        normalHours: attendance.NormalHours ?? '0.00',
        lateMinutes: attendance.LateMinutes ?? '0',
        preShiftOTHours: attendance.PreShiftOTHours ?? '0.00',
        postOTHours: attendance.PostOTHours ?? '0.00',
        prePostOTHours: attendance.PrePostOTHours ?? '0.00',
        holidayOTHours: attendance.HolidayOTHours ?? '0.00',
        sundayOTHours: attendance.SundayOTHours ?? '0.00',
      }];
    });
  }, [currentRows, employees, otAttendanceRows]);

  const typeOptions = useMemo(
    () =>
      Array.from(new Set(mapped.map((r) => r.otTypeLabel).filter(Boolean))).sort(),
    [mapped]
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();

    return mapped.filter((r) => {
      if (typeFilter !== 'All' && r.otTypeLabel !== typeFilter) return false;
      if (term) {
        const haystack = `${r.id} ${r.employeeId} ${r.employeeName} ${r.date} ${r.otTypeLabel}`.toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      return true;
    });
  }, [mapped, typeFilter, search]);


  const sorted = useMemo(() => {
    const list = [...filtered];
    const { key, dir } = sort;
    const mult = dir === 'asc' ? 1 : -1;

    const asNum = (v) => {
      if (v == null || v === '') return null;
      if (typeof v === 'number') return Number.isFinite(v) ? v : null;
      if (typeof v === 'string' && !Number.isNaN(Number(v))) return Number(v);
      return null;
    };

    list.sort((a, b) => {
      const av = a[key];
      const bv = b[key];
      const an = asNum(av);
      const bn = asNum(bv);
      if (an != null && bn != null) return (an - bn) * mult;
      if (typeof av === 'boolean' && typeof bv === 'boolean') {
        return (av === bv ? 0 : av ? 1 : -1) * mult;
      }
      return String(av ?? '').localeCompare(String(bv ?? '')) * mult;
    });
    return list;
  }, [filtered, sort]);

  const buildFilterParams = () => {
    const params = {};
    const empId = resolveEmployeeId();
    if (empId) params.employeeId = empId;

    if (dateMode === 'month') {
      if (!attMonth.trim()) return null;
      params.attDay = `${attMonth.trim()}-01`;
    } else {
      if (!dateFrom || !dateTo) return null;
      if (dateFrom > dateTo) return null;
      params.fromDate = dateFrom;
      params.toDate = dateTo;
    }
    return params;
  };

  /* Fixed horizontal scrollbar */
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return undefined;

    const measure = () => {
      setScrollWidth(wrap.scrollWidth);
      setHasOverflow(wrap.scrollWidth > wrap.clientWidth + 1);
    };

    measure();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }

    const ro = new ResizeObserver(measure);
    ro.observe(wrap);
    if (wrap.firstElementChild) ro.observe(wrap.firstElementChild);
    return () => ro.disconnect();
  }, [loading, sorted.length]);

  const handleWrapScroll = (e) => {
    const left = e.currentTarget.scrollLeft;

    if (headRef.current && headRef.current.scrollLeft !== left) {
      headRef.current.scrollLeft = left;
    }

    if (syncingBar.current) { syncingBar.current = false; return; }

    if (barRef.current && barRef.current.scrollLeft !== left) {
      syncingBar.current = true;
      barRef.current.scrollLeft = left;
    }
  };

  const handleHeadWheel = (e) => {
    const wrap = wrapRef.current;
    if (wrap && e.deltaX) wrap.scrollLeft += e.deltaX;
  };

  const handleBarScroll = (e) => {
    if (syncingBar.current) { syncingBar.current = false; return; }

    const left = e.currentTarget.scrollLeft;
    const wrap = wrapRef.current;
    if (wrap && wrap.scrollLeft !== left) {
      syncingBar.current = true;
      wrap.scrollLeft = left;
    }
    if (headRef.current && headRef.current.scrollLeft !== left) {
      headRef.current.scrollLeft = left;
    }
  };

  const approvableRows = useMemo(() => sorted.filter((r) => !r.isApproved), [sorted]);

  const allApprovableSelected =
    approvableRows.length > 0 &&
    approvableRows.every((r) => selectedRows.includes(r.id));

  const handleSelectAll = () => {
    if (allApprovableSelected) {
      setSelectedRows([]);
    } else {
      setSelectedRows(approvableRows.map((r) => r.id));
    }
  };

  const handleRowSelect = (rowId) => {
    setSelectedRows((prev) =>
      prev.includes(rowId) ? prev.filter((id) => id !== rowId) : [...prev, rowId]
    );
  };

  const handleApprove = (record) => {
    if (!record.otId) return;
    dispatch(
      approveOTRecord({
        employeeId: record.employeeId,
        overtimeId: record.otId,
        approvedBy: currentUserId,
      })
    );
    setSelectedRows((prev) => prev.filter((id) => id !== record.id));
  };

  const handleBulkApprove = () => {
    if (selectedRows.length === 0) return;

    const allRows = [...rows, ...pendingRows, ...approvedRows];
    const recordsById = new Map();
    allRows.forEach((r) => {
      if (r.HOT_OVERTIME_ID) {
        recordsById.set(String(r.HOT_OVERTIME_ID), r);
      }
    });

    const records = selectedRows
      .map((id) => {
        const r = recordsById.get(String(id));
        if (!r) return null;
        return {
          employeeId: r.HOT_EMPLOYEE_ID || '',
          overtimeId: r.HOT_OVERTIME_ID,
        };
      })
      .filter(Boolean);

    if (records.length === 0) return;

    dispatch(
      bulkApproveOTRecords({
        records,
        approvedBy: currentUserId,
      })
    );

    setSelectedRows([]);
  };

  /* All totals for the strip above the table */
  const allTotals = useMemo(() => {
    const r2 = (n) => Math.round(n * 100) / 100;
    return {
      totalHours: r2(sorted.reduce((s, r) => s + Number(r.totalHours || 0), 0)),
      normalHours: r2(sorted.reduce((s, r) => s + Number(r.normalHours || 0), 0)),
      preOT: r2(sorted.reduce((s, r) => s + Number(r.preShiftOTHours || 0), 0)),
      postOT: r2(sorted.reduce((s, r) => s + Number(r.postOTHours || 0), 0)),
      holidayOT: r2(sorted.reduce((s, r) => s + Number(r.holidayOTHours || 0), 0)),
      sundayOT: r2(sorted.reduce((s, r) => s + Number(r.sundayOTHours || 0), 0)),
      otHours: r2(sorted.reduce((s, r) => s + Number(r.otHours || 0), 0)),
    };
  }, [sorted]);

  const handleExport = () => {
    if (!sorted.length) {
      console.warn('No OT rows available to export.');
      return;
    }

    exportOTRecordsToExcel({
      rows: sorted,
      filters: {
        'Employee ID': selectedEmployee?.employeeId || employeeId || 'All',
        'Employee Name': selectedEmployee?.name || 'All',
        'Date Filter':
          dateMode === 'month'
            ? `Month: ${attMonth || 'All'}`
            : `Range: ${dateFrom || '…'} → ${dateTo || '…'}`,
        Tab: activeTab,
        'OT Type': typeFilter,
        Search: search || 'None',
      },
    });
  };

  const isSearchDisabled =
    dateMode === 'month'
      ? (!employeeId.trim() && !attMonth.trim() && !selectedEmployee)
      : (!employeeId.trim() && (!dateFrom || !dateTo || dateFrom > dateTo) && !selectedEmployee);
  const isBulkApproveDisabled = selectedRows.length === 0 || approving;

  const pendingCount = pendingRows.length;
  const approvedCount = approvedRows.length;

  const SortTH = ({ k, children, info }) => {
    const active = sort.key === k;
    const Icon = active
      ? (sort.dir === 'asc' ? ChevronUp : ChevronDown)
      : ChevronsUpDown;

    return (
      <th
        onClick={() => handleSort(k)}
        className={`${TH_BASE} cursor-pointer select-none hover:bg-gray-100`}
        aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      >
        <span className="inline-flex items-center justify-center gap-1.5">
          {children}
          <Icon size={12} className={active ? 'text-primary-600' : 'text-gray-300'} />
          {info && (
            <span onClick={(e) => e.stopPropagation()} className="inline-flex items-center">
              <InfoTip>{info}</InfoTip>
            </span>
          )}
        </span>
      </th>
    );
  };

  /* Header cell class — title case, tight padding, no uppercase */
  const TH_BASE =
    'whitespace-nowrap border-b border-gray-200 bg-gray-50 px-2 py-2 text-center text-xs font-semibold text-gray-600';

  return (
    <div className="p-2 space-y-4">
      <style>{SCROLL_STYLES}</style>

      <Breadcrumb
        trail={['Overtime Management', 'Overtime Details']}
        title="Overtime Details"
        action={
          <div className="flex items-center gap-2">
            {selectedRows.length > 1 && activeTab !== 'Approved' && (
              <button
                onClick={handleBulkApprove}
                disabled={isBulkApproveDisabled}
                className="flex items-center gap-2 h-9 px-4 bg-green-600 text-white rounded-lg text-xs font-semibold hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {approving ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Check size={14} />
                )}
                Approve Selected ({selectedRows.length})
              </button>
            )}
            <button
              onClick={handleExport}
              disabled={sorted.length === 0}
              className={BREADCRUMB_EXPORT_BTN}
            >
              <FileSpreadsheet size={15} />
              Export to Excel
            </button>
          </div>
        }
      />

      {/* Filters */}
      <div className="bg-white border border-gray-100 rounded-xl px-4 py-3.5 shadow-sm space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <span className={LABEL}>Employee</span>
            <EmployeeSearch
              value={selectedEmployee}
              onSelect={setSelectedEmployee}
              employees={employees}
              loading={employeesLoading}
              showAllOption={true}
            />
          </div>
          <div>
            <span className={LABEL}>Date Filter</span>

            <div className="flex items-stretch gap-1.5">
              {/* Toggle — fixed width on the left */}
              <div className="inline-flex h-9 shrink-0 items-center rounded-lg bg-slate-100 p-0.5 ring-1 ring-inset ring-slate-200/60">
                {['month', 'range'].map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => {
                      setDateMode(m);
                      if (m === 'range' && (!dateFrom || !dateTo)) {
                        const { from, to } = defaultRange();
                        setDateFrom(from);
                        setDateTo(to);
                      }
                    }}
                    className={`h-full rounded-md px-2.5 text-[10px] font-semibold transition-colors ${dateMode === m
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                      }`}
                  >
                    {m === 'month' ? 'Month' : 'Range'}
                  </button>
                ))}
              </div>

              {/* Input(s) — fill the rest of the row */}
              <div className="min-w-0 flex-1">
                {dateMode === 'month' ? (
                  <input
                    type="month"
                    value={attMonth}
                    onChange={(e) => setAttMonth(e.target.value)}
                    className={FIELD}
                  />
                ) : (
                  <div className="grid grid-cols-2 gap-1.5">
                    <input
                      type="date"
                      value={dateFrom}
                      max={dateTo || undefined}
                      onChange={(e) => setDateFrom(e.target.value)}
                      className={FIELD}
                    />
                    <input
                      type="date"
                      value={dateTo}
                      min={dateFrom || undefined}
                      onChange={(e) => setDateTo(e.target.value)}
                      className={FIELD}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
          <div>
            <span className={LABEL}>OT Type</span>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className={FIELD}
            >
              <option value="All">All</option>
              {typeOptions.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end gap-2">
            <button
              onClick={handleSearch}
              disabled={loading || isSearchDisabled}
              className="flex items-center gap-2 h-9 px-4 bg-primary-600 text-white rounded-lg text-xs font-semibold hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Search size={14} />
              {loading ? 'Loading...' : 'Search'}
            </button>
            <button
              onClick={handleReset}
              className="flex items-center gap-2 h-9 px-3 bg-gray-100 text-gray-600 rounded-lg text-xs font-semibold hover:bg-gray-200 transition-colors"
            >
              <RotateCcw size={14} />
              Reset
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between">
          {approvalError && (
            <span className="text-xs text-red-600">{approvalError}</span>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Tabs and Table */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        {/* Sticky wrapper: cards + tab controls + table header stick together */}
        <div
          className="sticky z-20 overflow-hidden rounded-t-xl bg-white"
          style={{ top: STICKY_TOP }}
        >
          {/* Totals strip */}
          {!loading && sorted.length > 0 && (
            <div className="grid grid-cols-2 gap-2 border-b border-slate-200 bg-gradient-to-r from-slate-50/70 to-white px-3 py-2.5 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7">
              <StatCard label="Total hours" value={fmtH(allTotals.totalHours)} />
              <StatCard label="Normal hours" value={fmtH(allTotals.normalHours)} />
              <StatCard label="Total OT hours" value={fmtH(allTotals.otHours)} tone="emerald" />
              <StatCard label="Pre OT hours" value={fmtH(allTotals.preOT)} />
              <StatCard label="Post OT hours" value={fmtH(allTotals.postOT)} />
              <StatCard label="Holiday OT hours" value={fmtH(allTotals.holidayOT)} />
              <StatCard label="Sunday OT hours" value={fmtH(allTotals.sundayOT)} />
            </div>
          )}

          {/* Tab controls — Date sort + segmented tabs, right-aligned */}
          <div className="flex flex-col gap-3 border-b border-slate-200/70 bg-gradient-to-r from-white to-slate-50/40 px-5 py-3.5 sm:flex-row sm:items-center sm:justify-end">
            <div className="flex flex-wrap items-center gap-2.5">
              {/* Date sort chip */}
              <button
                type="button"
                onClick={toggleDateSort}
                title={
                  isDateActive && sort.dir === 'asc'
                    ? 'Sorted oldest → newest. Click for newest → oldest.'
                    : 'Sorted newest → oldest. Click for oldest → newest.'
                }
                className={`group inline-flex h-8 items-center gap-1.5 rounded-lg border px-3 text-xs font-semibold transition-all active:scale-[0.98] ${isDateActive
                  ? 'border-primary-200 bg-primary-50 text-primary-700 shadow-sm hover:bg-primary-100'
                  : 'border-slate-200 bg-white text-slate-600 shadow-sm hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900'
                  }`}
              >
                {isDateActive ? (
                  sort.dir === 'asc'
                    ? <ChevronUp size={13} />
                    : <ChevronDown size={13} />
                ) : (
                  <ArrowUpDown size={13} className="text-slate-400 group-hover:text-slate-500" />
                )}
                <span className="tracking-tight">Date</span>
                {isDateActive && (
                  <span className="ml-0.5 text-[10px] font-bold tracking-widest opacity-60">
                    {sort.dir === 'asc' ? 'ASC' : 'DESC'}
                  </span>
                )}
              </button>

              {/* Divider */}
              <div className="hidden h-5 w-px bg-slate-200 sm:block" aria-hidden="true" />

              {/* Segmented tabs */}
              <div
                role="tablist"
                aria-label="Overtime record tabs"
                className="inline-flex items-center gap-0.5 rounded-xl bg-slate-100/80 p-1 ring-1 ring-inset ring-slate-200/60"
              >
                {TABS.map((tab) => {
                  const active = activeTab === tab;
                  let count = 0;
                  if (tab === 'Pending') count = pendingCount;
                  else if (tab === 'Approved') count = approvedCount;
                  else count = rows.length;

                  const badgeClass = active
                    ? tab === 'Approved'
                      ? 'bg-emerald-100 text-emerald-700'
                      : tab === 'Pending'
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-slate-200 text-slate-700'
                    : 'bg-slate-200/70 text-slate-500';

                  return (
                    <button
                      key={tab}
                      role="tab"
                      aria-selected={active}
                      onClick={() => setActiveTab(tab)}
                      className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-all active:scale-[0.98] ${active
                        ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-900/5'
                        : 'text-slate-500 hover:text-slate-700'
                        }`}
                    >
                      <span>{tab}</span>
                      <span
                        className={`inline-flex min-w-[18px] items-center justify-center rounded-full px-1.5 text-[10px] font-bold tabular-nums transition-colors ${badgeClass}`}
                      >
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Table header — only when not loading */}
          {!loading && (
            <div
              onWheel={handleHeadWheel}
              className="flex border-b border-gray-200 bg-gray-50"
            >
              <div className="shrink-0 bg-gray-50" style={{ width: PINNED_WIDTH }}>
                <table
                  style={{ width: PINNED_WIDTH, tableLayout: 'fixed' }}
                  className="border-separate border-spacing-0 text-sm"
                >
                  <ColGroup widths={COLUMN_WIDTHS.slice(0, PINNED_COLS)} />
                  <thead>
                    <tr>
                      <th className="bg-gray-50 px-2 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={allApprovableSelected}
                          onChange={handleSelectAll}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          disabled={activeTab === 'Approved' || approvableRows.length === 0}
                        />
                      </th>
                      <th className={TH_BASE}>Employee</th>
                      <th className={TH_BASE}>Date</th>
                      <th className={TH_BASE}>OT type</th>
                      <th className={TH_BASE}>In time</th>
                      <th className={TH_BASE}>Out time</th>
                    </tr>
                  </thead>
                </table>
              </div>

              <div ref={headRef} className="min-w-0 flex-1 overflow-hidden">
                <table style={tableStyleFor(SCROLL_WIDTHS)} className="border-separate border-spacing-0 text-sm">
                  <ColGroup widths={SCROLL_WIDTHS} />
                  <thead>
                    <tr>
                      <th className={TH_BASE}>
                        <span className="inline-flex items-center justify-center gap-1.5">
                          Late hours
                          <InfoTip>
                            <span className="font-semibold">Late hours</span> — duration between shift
                            start and actual IN punch, shown as decimal hours
                          </InfoTip>
                        </span>
                      </th>

                      <th className={TH_BASE}>
                        <span className="inline-flex items-center justify-center gap-1.5">
                          Total hours
                          <InfoTip>
                            <span className="font-semibold">Total hours</span> = Normal hours +
                            OT hours (including Pre-OT and Post-OT). Shown as{' '}
                            <span className="font-mono">Hh MMm</span>.
                          </InfoTip>
                        </span>
                      </th>

                      <th className={TH_BASE}>
                        <span className="inline-flex items-center justify-center gap-1.5">
                          Normal hours
                          <InfoTip>
                            <span className="font-semibold">Normal hours</span> — regular working hours
                            inside the standard shift. Shown as{' '}
                            <span className="font-mono">Hh MMm</span>.
                          </InfoTip>
                        </span>
                      </th>

                      <th className={TH_BASE}>
                        <span className="inline-flex items-center justify-center gap-1.5">
                          Pre OT hours
                          <InfoTip>
                            <span className="font-semibold">Pre-OT hours</span> — overtime worked
                            before the standard shift start. Shown as{' '}
                            <span className="font-mono">Hh MMm</span>.
                          </InfoTip>
                        </span>
                      </th>

                      <th className={TH_BASE}>
                        <span className="inline-flex items-center justify-center gap-1.5">
                          Post OT hours
                          <InfoTip>
                            <span className="font-semibold">Post-OT hours</span> — overtime
                            worked after the standard shift end. Shown as{' '}
                            <span className="font-mono">Hh MMm</span>.
                          </InfoTip>
                        </span>
                      </th>

                      <th className={TH_BASE}>
                        <span className="inline-flex items-center justify-center gap-1.5">
                          Holiday OT hours
                          <InfoTip>
                            <span className="font-semibold">Holiday OT hours</span> — overtime
                            worked on a declared holiday (Poya / Public / Mercantile). Shown as{' '}
                            <span className="font-mono">Hh MMm</span>.
                          </InfoTip>
                        </span>
                      </th>

                      <th className={TH_BASE}>
                        <span className="inline-flex items-center justify-center gap-1.5">
                          Sunday OT hours
                          <InfoTip>
                            <span className="font-semibold">Sunday OT hours</span> — overtime
                            worked on a Sunday. Shown as{' '}
                            <span className="font-mono">Hh MMm</span>.
                          </InfoTip>
                        </span>
                      </th>

                      <th className={TH_BASE}>
                        <span className="inline-flex items-center justify-center gap-1.5">
                          OT hours
                          <InfoTip>
                            <span className="font-semibold">OT hours</span> — total overtime
                            hours (Pre-OT + Post-OT) beyond the standard shift. Shown as{' '}
                            <span className="font-mono">Hh MMm</span>.
                          </InfoTip>
                        </span>
                      </th>

                      <th className={TH_BASE}>
                        <span className="inline-flex items-center justify-center gap-1.5">
                          OT rate
                          <InfoTip>
                            <span className="font-semibold">OT rate</span> — the hourly pay rate
                            used to calculate the OT amount.
                          </InfoTip>
                        </span>
                      </th>

                      <th className={TH_BASE}>
                        <span className="inline-flex items-center justify-center gap-1.5">
                          OT amount
                          <InfoTip>
                            <span className="font-semibold">OT amount</span> = Adjusted OT hours
                            × OT rate.
                          </InfoTip>
                        </span>
                      </th>

                      <th className={TH_BASE}>Status</th>
                      <th className={TH_BASE}>Actions</th>
                    </tr>
                  </thead>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Body */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={32} className="animate-spin text-primary-600" />
          </div>
        ) : (
          <>
            <div
              ref={wrapRef}
              onScroll={handleWrapScroll}
              className="ot-table-scroll overflow-x-auto"
            >
              <table style={tableStyleFor(COLUMN_WIDTHS)} className="border-separate border-spacing-0 text-sm">
                <ColGroup />
                <tbody>
                  {sorted.length === 0 && (
                    <tr>
                      <td colSpan={COLUMN_WIDTHS.length} className="px-5 py-10 text-center text-sm text-gray-400">
                        {activeTab === 'Pending' && pendingCount === 0
                          ? 'No pending OT records found'
                          : activeTab === 'Approved' && approvedCount === 0
                            ? 'No approved OT records found'
                            : 'No OT records found'}
                      </td>
                    </tr>
                  )}

                  {sorted.map((r, index) => {
                    const isPending = !r.isApproved;

                    const rowBg = 'bg-white group-hover:bg-gray-50';

                    return (
                      <tr key={r.id || `row-${index}`} className="group">
                        <td
                          style={{ position: 'sticky', left: PINNED_LEFT[0], zIndex: 10 }}
                          className={`border-b border-gray-100 ${rowBg} px-2 py-2 text-center`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedRows.includes(r.id)}
                            onChange={() => handleRowSelect(r.id)}
                            disabled={!isPending}
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                        </td>

                        <td
                          style={{ position: 'sticky', left: PINNED_LEFT[1], zIndex: 10 }}
                          className={`border-b border-gray-100 ${rowBg} px-2 py-2`}
                        >
                          <p
                            className="font-medium text-gray-900 line-clamp-2 break-words"
                            title={r.employeeName || ''}
                          >
                            {r.employeeName || '—'}
                          </p>
                        </td>

                        <td
                          style={{ position: 'sticky', left: PINNED_LEFT[2], zIndex: 10 }}
                          className={`whitespace-nowrap border-b border-gray-100 ${rowBg} px-2 py-2 text-center text-gray-600 tabular-nums`}
                        >
                          {r.date || '—'}
                        </td>

                        <td
                          style={{ position: 'sticky', left: PINNED_LEFT[3], zIndex: 10 }}
                          className={`border-b border-gray-100 px-2 py-2 text-center font-semibold text-gray-800 ${rowBg}`}
                        >
                          {r.otTypeLabel || '—'}
                        </td>

                        <td
                          style={{ position: 'sticky', left: PINNED_LEFT[4], zIndex: 10 }}
                          className={`border-b border-gray-100 px-2 py-2 text-center text-gray-600 tabular-nums ${rowBg}`}
                        >
                          {r.inTime || '—'}
                        </td>

                        <td
                          style={{ position: 'sticky', left: PINNED_LEFT[5], zIndex: 10 }}
                          className={`border-b border-gray-100 px-2 py-2 text-center text-gray-600 tabular-nums ${rowBg}`}
                        >
                          {r.outTime || '—'}
                        </td>

                        <td className={`whitespace-nowrap border-b border-gray-100 px-2 py-2 text-center ${rowBg}`}>
                          <span className={Number(r.lateMinutes) > 0 ? 'font-semibold text-amber-700' : 'text-gray-600'}>
                            {fmtLateHours(r.lateMinutes)}
                          </span>
                        </td>

                        <td className={`whitespace-nowrap border-b border-gray-100 px-2 py-2 text-center text-gray-600 ${rowBg}`}>
                          {fmtH(r.totalHours)}
                        </td>

                        <td className={`whitespace-nowrap border-b border-gray-100 px-2 py-2 text-center text-gray-600 ${rowBg}`}>
                          {fmtH(r.normalHours)}
                        </td>

                        <td className={`whitespace-nowrap border-b border-gray-100 px-2 py-2 text-center text-gray-600 ${rowBg}`}>
                          {fmtH(r.preShiftOTHours)}
                        </td>

                        <td className={`whitespace-nowrap border-b border-gray-100 px-2 py-2 text-center text-gray-600 ${rowBg}`}>
                          {fmtH(r.postOTHours)}
                        </td>

                        <td className={`whitespace-nowrap border-b border-gray-100 px-2 py-2 text-center text-gray-600 ${rowBg}`}>
                          {fmtH(r.holidayOTHours)}
                        </td>

                        <td className={`whitespace-nowrap border-b border-gray-100 px-2 py-2 text-center text-gray-600 ${rowBg}`}>
                          {fmtH(r.sundayOTHours)}
                        </td>

                        <td className={`border-b border-gray-100 px-2 py-2 text-center font-medium text-primary-700 ${rowBg}`}>
                          {fmtH(r.otHours)}
                        </td>

                        <td className={`whitespace-nowrap border-b border-gray-100 px-2 py-2 text-center text-gray-600 tabular-nums ${rowBg}`}>
                          {Number(r.otRate ?? 0).toFixed(2)}
                        </td>
                        <td className={`whitespace-nowrap border-b border-gray-100 px-2 py-2 text-center font-semibold text-emerald-700 tabular-nums ${rowBg}`}>
                          {Number(r.otAmount ?? 0).toFixed(2)}
                        </td>
                        <td className={`border-b border-gray-100 px-2 py-2 text-center ${rowBg}`}>
                          <StatusBadge isApproved={r.isApproved} />
                        </td>
                        <td className={`border-b border-gray-100 px-2 py-3 text-center ${rowBg}`}>
                          {r.isApproved ? (
                            <span className="text-xs text-gray-400">—</span>
                          ) : (
                            <button
                              onClick={() => handleApprove(r)}
                              disabled={approving}
                              className="inline-flex items-center justify-center gap-1 rounded-lg bg-blue-50 px-3 py-1 text-xs font-medium text-blue-600 transition-colors hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              {approving ? (
                                <Loader2 size={14} className="animate-spin" />
                              ) : (
                                <>
                                  <CheckCircle size={14} />
                                  Approve
                                </>
                              )}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Fixed horizontal scrollbar */}
            {hasOverflow && (
              <div
                style={{ position: 'sticky', bottom: -20, zIndex: 10 }}
                className="bg-white border-t border-gray-200"
              >
                <div
                  ref={barRef}
                  onScroll={handleBarScroll}
                  className="fixed-scroll overflow-x-auto overflow-y-hidden"
                >
                  <div style={{ width: scrollWidth, height: 14 }} />
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}